<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div style="max-width:760px">
      <h3 class="mb-1">Unimicro Config</h3>
      <div style="font-style: italic; color:var(--bo-muted)">
        CAGISTAs <strong>serverklient</strong> hos Unimicro — én client id og ett sertifikat for alle merchants og
        begge miljøer. Merchanten gir oss tilgang ved å aktivere CAGISTA i Unimicro → Markedsplass; da
        oppretter Unimicro en serverbruker på selskapet, og Unimicro sender aktiveringen til
        <code>/api/public/unimicroActivation</code>. Lagres i Couchbase; sertifikat og passord
        <strong>kryptert</strong>.
      </div>

      <div class="alert alert-info mt-3 py-2" style="font-size:0.88rem">
        <font-awesome-icon :icon="['fas', 'lock']" />
        Sertifikat, passord og signeringsnøkkel vises aldri her igjen etter lagring. La feltene stå
        <strong>tomme</strong> for å beholde det som er lagret — fyll dem kun ut for å erstatte.
      </div>

      <config-lock ref="lock" :configured="isConfigured">
      <h5 class="mt-4 mb-1">Miljø</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Miljø</label>
          <select class="form-select" v-model="form.environment">
            <option value="TEST">TEST — test.unimicro.no</option>
            <option value="PROD">PROD — app.unimicro.no</option>
          </select>
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Bestemmer innloggingsserver og API-base. Samme sertifikat og client id i begge — kun URL-ene
            skiller. Bare Unimicros egen plattform; DNB, Azets og SpareBank 1 er utenfor.
          </span>
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Innlogging / API</label>
          <!-- Follows the dropdown, not the saved value: these two URLs ARE what the choice above
               means, so showing yesterday's pair while the selector says something else is the one
               thing this box must not do. -->
          <div><code>{{ loginServerUrl }}</code></div>
          <div><code>{{ apiBaseUrl }}</code></div>
          <span v-if="form.environment !== config.environment" style="color:var(--bo-brand-ink); font-size:0.78rem">
            Ikke lagret ennå.
          </span>
        </div>
      </div>

      <h5 class="mt-4 mb-1">Serverklient (Developer Portal → Applications → Server client)</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Client ID</label>
          <input class="form-control" type="text" v-model="form.clientId" autocomplete="off" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Scope</label>
          <input class="form-control" type="text" v-model="form.scope" placeholder="AppFramework" />
          <span style="color:var(--bo-muted); font-size:0.78rem">Serverklienten trenger bare <code>AppFramework</code> — ingen offline_access.</span>
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Sertifikat (.p12 / .pfx)</label>
          <div class="mb-1">
            <span class="badge" :class="config.certificateSet ? 'bg-success' : 'bg-secondary'">
              {{ config.certificateSet ? ('Lagret ✓ ' + (config.certificateFileName || '')) : 'Ikke lastet opp' }}
            </span>
          </div>
          <input class="form-control" type="file" accept=".p12,.pfx" @change="onCertificateChosen" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Samme fil som er registrert på serverklienten i Developer Portal. Leses her i nettleseren og
            sendes kryptert til serveren.
          </span>
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Sertifikatpassord</label>
          <div class="mb-1">
            <span class="badge" :class="config.certificatePasswordSet ? 'bg-success' : 'bg-secondary'">
              {{ config.certificatePasswordSet ? 'Lagret ✓' : 'Ikke satt' }}
            </span>
          </div>
          <input type="password" class="form-control" v-model="form.certificatePassword" autocomplete="new-password"
                 :placeholder="config.certificatePasswordSet ? '•••• (lagret — la stå tomt)' : ''" />
        </div>
      </div>

      <h5 class="mt-4 mb-1">Produkt i markedsplassen</h5>
      <div class="row g-3 mt-0">
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Produkt-ID (TEST)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.productIdTest" />
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Produkt-ID (PROD)</label>
          <input class="form-control" type="number" min="0" v-model.number="form.productIdProd" />
        </div>
        <div class="col-12">
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Fra Developer Portal → produktet. Kan være forskjellig i test og produksjon. Brukes i
            <code>PUT api/elsa/purchases/{id}?action=set-as-active</code> når en merchant fullfører oppsettet.
            Sett produktets startstatus til <strong>Pending</strong> i portalen — da er det vi som aktiverer
            når alt er klart.
          </span>
        </div>
        <div class="col-md-6">
          <label class="form-label mb-1 sub-label">Signeringsnøkkel for aktiverings-webhook (valgfri)</label>
          <div class="mb-1">
            <span class="badge" :class="config.activationSigningKeySet ? 'bg-success' : 'bg-secondary'">
              {{ config.activationSigningKeySet ? 'Verifiserer signatur ✓' : 'Ikke satt — signatur sjekkes ikke' }}
            </span>
          </div>
          <input type="password" class="form-control" v-model="form.activationSigningKey" autocomplete="new-password" />
          <span style="color:var(--bo-muted); font-size:0.78rem">
            Om Unimicro signerer aktiveringen (<code>Unimicro-Signature</code>), legg nøkkelen her. Uten nøkkel
            logges headeren, og selskapet bekreftes uansett mot <code>/api/init/companies</code> før det lagres.
          </span>
        </div>
      </div>

      <div class="mt-4 d-flex gap-3 flex-wrap">
        <button type="button" class="bo_btn" :disabled="saving" @click="save">{{ saving ? 'Lagrer…' : 'Lagre' }}</button>
        <button type="button" class="bo_btn_secondary" :disabled="testing || !config.configured" @click="test">{{ testing ? 'Tester…' : 'Test innlogging' }}</button>
      </div>
      </config-lock>

      <div v-if="message" class="mt-3" :class="messageOk ? 'message-success' : 'message-error'">{{ message }}</div>
      <ul v-if="companies.length" class="mt-2 small">
        <li v-for="c in companies" :key="c">{{ c }}</li>
      </ul>
      <p v-if="certificateInfo" class="mt-2 small" style="color:var(--bo-muted)">Sertifikat: {{ certificateInfo }}</p>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfigLock from "@/components/other/ConfigLock";
import SettingsService from "@/services/settings/SettingsService";

export default {
  name: 'UnimicroConfig',
  components: { LoadingSpinner, ConfigLock },
  data() {
    return {
      loading: true,
      saving: false,
      testing: false,
      message: '',
      messageOk: false,
      companies: [],
      certificateInfo: '',
      config: { environment: 'TEST', loginServerUrl: '', apiBaseUrl: '', certificateSet: false, certificatePasswordSet: false, activationSigningKeySet: false, configured: false },
      form: { environment: 'TEST', clientId: '', scope: 'AppFramework', certificateP12Base64: '', certificateFileName: '', certificatePassword: '', productIdTest: 0, productIdProd: 0, activationSigningKey: '' }
    };
  },
  computed: {
    isConfigured() { return !!this.config.configured; },
    /*
     * Mirrors UnimicroConfigService's constants. The server sends the pair for the SAVED environment,
     * which is the right answer to «what is in use» and the wrong one while somebody is choosing —
     * the box sat on test.unimicro.no with PROD selected. Kept in step with the backend by being two
     * literals in both places; if a third environment ever appears, it appears in both.
     */
    isProdSelected() { return this.form.environment === 'PROD'; },
    loginServerUrl() {
      return this.isProdSelected ? 'https://login.unimicro.no' : 'https://test-login.unimicro.no';
    },
    apiBaseUrl() {
      return this.isProdSelected ? 'https://app.unimicro.no' : 'https://test.unimicro.no';
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      SettingsService.getUnimicroConfig().then(
        (response) => {
          this.config = response.data || this.config;
          this.form.environment = this.config.environment || 'TEST';
          this.form.clientId = this.config.clientId || '';
          this.form.scope = this.config.scope || 'AppFramework';
          this.form.productIdTest = this.config.productIdTest || 0;
          this.form.productIdProd = this.config.productIdProd || 0;
          this.form.certificateP12Base64 = '';
          this.form.certificateFileName = '';
          this.form.certificatePassword = '';
          this.form.activationSigningKey = '';
          this.loading = false;
          this.$nextTick(() => this.$refs.lock && this.$refs.lock.reset());
        },
        (error) => { console.log(error); this.loading = false; }
      );
    },
    // The .p12 is read in the browser and carried as base64; the server encrypts it before storing.
    onCertificateChosen(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || '');
        this.form.certificateP12Base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
        this.form.certificateFileName = file.name;
      };
      reader.readAsDataURL(file);
    },
    save() {
      this.saving = true;
      this.message = '';
      this.companies = [];
      SettingsService.saveUnimicroConfig(this.form).then(
        (response) => {
          this.saving = false;
          this.config = response.data || this.config;
          this.message = 'Lagret.';
          this.messageOk = true;
          this.load();
        },
        (error) => {
          this.saving = false;
          this.message = (error.response && error.response.data && error.response.data.error) || 'Lagring feilet.';
          this.messageOk = false;
        }
      );
    },
    test() {
      this.testing = true;
      this.message = '';
      this.companies = [];
      this.certificateInfo = '';
      SettingsService.testUnimicroConfig().then(
        (response) => {
          this.testing = false;
          const d = response.data || {};
          this.message = d.message || '';
          this.messageOk = !!d.success;
          this.companies = d.companies || [];
          this.certificateInfo = d.certificate || '';
        },
        (error) => { console.log(error); this.testing = false; this.message = 'Testen feilet.'; this.messageOk = false; }
      );
    }
  }
};
</script>
